import React, { useState, useEffect, useCallback, useRef } from 'react';
import Layout from '../components/Layout';
import api from '../services/api';
import { toast } from 'react-toastify';
import * as XLSX from 'xlsx';

const ESTADO_BADGE = {
  borrador: { label: 'Borrador', bg: '#f1f5f9', color: '#475569' },
  enviando: { label: 'Enviando', bg: '#dbeafe', color: '#1d4ed8' },
  enviada:  { label: 'Enviada',  bg: '#dcfce7', color: '#166534' },
  pausada:  { label: 'Pausada',  bg: '#fef3c7', color: '#92400e' },
};

const inputStyle = {
  border: '1.5px solid #e5e7eb', borderRadius: '8px', padding: '0.6rem 0.75rem',
  fontSize: '0.9rem', width: '100%', boxSizing: 'border-box', outline: 'none',
  backgroundColor: '#fafafa', fontFamily: 'inherit',
};

const FORM_EMPTY = { nombre: '', mensaje: '', mensajes_por_minuto: 6 };

const MEDIA_MAX_BYTES = { imagen: 8 * 1024 * 1024, video: 20 * 1024 * 1024 };

// /uploads/... son rutas relativas al backend — para mostrarlas o para que la extensión
// les haga fetch() hace falta el origen completo (la API vive en otro puerto/subruta
// en local, aunque en producción comparta dominio con el frontend).
const origenBackend = () => api.defaults.baseURL.replace(/\/api\/?$/, '');

const fileToBase64 = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

const keyDestinatario = (d) =>
  `${d.tipo}-${d.referencia_id ?? String(d.whatsapp_number || d.contacto || '').trim()}`;

const Difusion = () => {
  const [campanas, setCampanas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(FORM_EMPTY);
  const [filtroTipo, setFiltroTipo] = useState('ambos');
  const [etapas, setEtapas] = useState([]);
  const [filtroEtapa, setFiltroEtapa] = useState('');
  const [preview, setPreview] = useState([]);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [seleccionados, setSeleccionados] = useState({}); // key `${tipo}-${referencia_id}` -> item
  const [origenDestinatarios, setOrigenDestinatarios] = useState('crm'); // 'crm' | 'excel'
  const [resumenExcel, setResumenExcel] = useState(null); // { cargados, omitidosInvalidos, omitidosDuplicados } | null
  const [guardando, setGuardando] = useState(false);
  const [detalle, setDetalle] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);
  const pollRef = useRef(null);

  // Adjunto de campaña (opcional): desde un catálogo existente o subiendo un archivo
  const [adjuntoModo, setAdjuntoModo] = useState('ninguno'); // 'ninguno' | 'catalogo' | 'subir'
  const [adjunto, setAdjunto] = useState(null); // { url, tipo }
  const [catalogos, setCatalogos] = useState([]);
  const [catalogoActivo, setCatalogoActivo] = useState(null);
  const [productosCatalogo, setProductosCatalogo] = useState([]);
  const [subiendoArchivo, setSubiendoArchivo] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const fetchCampanas = useCallback(async () => {
    try {
      const { data } = await api.get('/campanas');
      setCampanas(data);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error al cargar campañas');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchEtapas = useCallback(async () => {
    try {
      const { data } = await api.get('/crm/etapas');
      setEtapas(data.filter(e => e.activo));
    } catch {}
  }, []);

  useEffect(() => { fetchCampanas(); fetchEtapas(); }, [fetchCampanas, fetchEtapas]);

  // Poll mientras haya alguna campaña "enviando"
  useEffect(() => {
    const hayEnviando = campanas.some(c => c.estado === 'enviando');
    if (hayEnviando) {
      const interval = setInterval(() => fetchCampanas(), 4000);
      return () => clearInterval(interval);
    }
  }, [campanas, fetchCampanas]);

  const abrirNueva = () => {
    setEditingId(null);
    setForm(FORM_EMPTY);
    setFiltroTipo('ambos');
    setFiltroEtapa('');
    setPreview([]);
    setSeleccionados({});
    setOrigenDestinatarios('crm');
    setResumenExcel(null);
    setAdjuntoModo('ninguno');
    setAdjunto(null);
    setCatalogoActivo(null);
    setProductosCatalogo([]);
    setShowModal(true);
  };

  const abrirEditar = async (campanaId) => {
    try {
      const { data } = await api.get(`/campanas/${campanaId}`);
      setEditingId(data.id);
      setForm({ nombre: data.nombre, mensaje: data.mensaje, mensajes_por_minuto: data.mensajes_por_minuto });
      setFiltroTipo('ambos');
      setFiltroEtapa('');
      setOrigenDestinatarios('crm');
      setResumenExcel(null);
      const preSeleccionados = {};
      (data.destinatarios || []).forEach(d => {
        const item = {
          tipo: d.tipo, referencia_id: d.referencia_id,
          nombres: d.nombre || d.contacto, apellidos: '', whatsapp_number: d.contacto,
        };
        preSeleccionados[keyDestinatario(item)] = item;
      });
      setSeleccionados(preSeleccionados);
      setPreview(Object.values(preSeleccionados));
      if (data.media_url) {
        setAdjuntoModo('subir');
        setAdjunto({ url: data.media_url, tipo: data.media_tipo });
      } else {
        setAdjuntoModo('ninguno');
        setAdjunto(null);
      }
      setCatalogoActivo(null);
      setProductosCatalogo([]);
      setShowModal(true);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error al cargar la campaña');
    }
  };

  const cargarCatalogos = async () => {
    try {
      const { data } = await api.get('/catalogos');
      setCatalogos(data);
    } catch (err) {
      toast.error('Error al cargar catálogos');
    }
  };

  const elegirModoAdjunto = (modo) => {
    setAdjuntoModo(modo);
    setAdjunto(null);
    if (modo === 'catalogo' && catalogos.length === 0) cargarCatalogos();
  };

  const abrirCatalogo = async (cat) => {
    setCatalogoActivo(cat);
    try {
      const { data } = await api.get(`/catalogos/${cat.id}/productos`);
      setProductosCatalogo(data.filter(p => p.imagen));
    } catch (err) {
      toast.error('Error al cargar productos del catálogo');
    }
  };

  const elegirProductoCatalogo = (prod) => {
    setAdjunto({ url: `${origenBackend()}${prod.imagen}`, tipo: 'imagen' });
  };

  const subirArchivoAdjunto = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    const esVideo = file.type.startsWith('video/');
    const tipo = esVideo ? 'video' : 'imagen';
    if (!file.type.startsWith('image/') && !esVideo) {
      toast.error('Solo se admiten imágenes o videos');
      return;
    }
    if (file.size > MEDIA_MAX_BYTES[tipo]) {
      toast.error(`El archivo supera el tope de ${MEDIA_MAX_BYTES[tipo] / (1024 * 1024)}MB para ${esVideo ? 'videos' : 'imágenes'}`);
      return;
    }

    setSubiendoArchivo(true);
    try {
      const base64 = await fileToBase64(file);
      const { data } = await api.post('/campanas/upload-media', { base64 });
      setAdjunto({ url: data.url, tipo: data.tipo });
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error al subir el archivo');
    } finally {
      setSubiendoArchivo(false);
    }
  };

  const cargarDestinatarios = async () => {
    setLoadingPreview(true);
    try {
      const params = new URLSearchParams({ tipo: filtroTipo });
      if (filtroEtapa) params.append('etapa', filtroEtapa);
      const { data } = await api.get(`/campanas/destinatarios-preview?${params}`);
      setPreview(data);
      const nuevos = {};
      data.forEach(d => { nuevos[keyDestinatario(d)] = d; });
      setSeleccionados(nuevos);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error al cargar destinatarios');
    } finally {
      setLoadingPreview(false);
    }
  };

  const cargarDestinatariosExcel = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    setLoadingPreview(true);
    try {
      const buffer = await file.arrayBuffer();
      const wb = XLSX.read(buffer, { type: 'array' });
      const filas = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]], { defval: '' });

      const normalizarHeader = (h) => h.toString().trim().toLowerCase().replace('é', 'e').replace('á', 'a');
      const items = [];
      const vistos = new Set();
      let omitidosInvalidos = 0;
      let omitidosDuplicados = 0;

      filas.forEach(fila => {
        const entradas = Object.entries(fila).reduce((acc, [k, v]) => { acc[normalizarHeader(k)] = v; return acc; }, {});
        const nombre = String(entradas.nombre ?? '').trim();
        const telefono = String(entradas.telefono ?? '').trim().replace(/[\s-]/g, '');

        if (!nombre || !telefono) { omitidosInvalidos++; return; }
        if (vistos.has(telefono)) { omitidosDuplicados++; return; }
        vistos.add(telefono);
        items.push({ tipo: 'manual', referencia_id: null, nombres: nombre, apellidos: '', whatsapp_number: telefono });
      });

      setPreview(items);
      const nuevos = {};
      items.forEach(d => { nuevos[keyDestinatario(d)] = d; });
      setSeleccionados(nuevos);
      setResumenExcel({ cargados: items.length, omitidosInvalidos, omitidosDuplicados });

      if (omitidosInvalidos) toast.warn(`${omitidosInvalidos} fila(s) sin nombre o teléfono fueron omitidas`);
      if (omitidosDuplicados) toast.warn(`${omitidosDuplicados} fila(s) con teléfono duplicado fueron omitidas`);
      if (!items.length) toast.error('El archivo no tiene filas válidas (se requieren columnas "nombre" y "telefono")');
    } catch (err) {
      toast.error('No se pudo leer el archivo. Verifica que sea un Excel (.xlsx) o CSV válido');
    } finally {
      setLoadingPreview(false);
    }
  };

  const toggleSeleccionado = (item) => {
    const key = keyDestinatario(item);
    setSeleccionados(prev => {
      const nuevo = { ...prev };
      if (nuevo[key]) delete nuevo[key]; else nuevo[key] = item;
      return nuevo;
    });
  };

  const totalSeleccionados = Object.keys(seleccionados).length;

  const crearCampana = async (enviarDeInmediato) => {
    if (!form.nombre.trim() || !form.mensaje.trim()) {
      toast.error('Nombre y mensaje son requeridos');
      return;
    }
    if (totalSeleccionados === 0) {
      toast.error('Carga y selecciona al menos un destinatario');
      return;
    }
    setGuardando(true);
    try {
      const destinatarios = Object.values(seleccionados).map(d => ({
        tipo: d.tipo,
        referencia_id: d.referencia_id,
        nombre: `${d.nombres}${d.apellidos ? ' ' + d.apellidos : ''}`.trim(),
        contacto: d.whatsapp_number,
      }));
      const payload = {
        nombre: form.nombre.trim(),
        mensaje: form.mensaje.trim(),
        mensajes_por_minuto: form.mensajes_por_minuto,
        destinatarios,
        ...(adjunto ? { media_url: adjunto.url, media_tipo: adjunto.tipo } : {}),
      };
      const { data } = editingId
        ? await api.put(`/campanas/${editingId}`, payload)
        : await api.post('/campanas', payload);
      if (enviarDeInmediato) {
        await api.post(`/campanas/${data.id}/enviar`);
        toast.success('Campaña enviándose');
      } else {
        toast.success('Campaña guardada como borrador');
      }
      setShowModal(false);
      fetchCampanas();
    } catch (err) {
      toast.error(err.response?.data?.message || err.response?.data?.error || 'Error al guardar la campaña');
    } finally {
      setGuardando(false);
    }
  };

  const enviarBorrador = async (id) => {
    try {
      await api.post(`/campanas/${id}/enviar`);
      toast.success('Campaña enviándose');
      fetchCampanas();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error al enviar');
    }
  };

  const pausarCampana = async (id) => {
    try {
      await api.post(`/campanas/${id}/pausar`);
      toast.success('Campaña pausada');
      fetchCampanas();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error al pausar');
    }
  };

  const reintentarCampana = async (id) => {
    try {
      await api.post(`/campanas/${id}/reintentar`);
      toast.success('Reintentando envío a los fallidos');
      fetchCampanas();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error al reintentar');
    }
  };

  const eliminarCampana = (id) => {
    setConfirmAction({
      mensaje: '¿Eliminar esta campaña? Se perderá el historial de envíos.',
      onConfirm: async () => {
        try {
          await api.delete(`/campanas/${id}`);
          toast.success('Campaña eliminada');
          fetchCampanas();
        } catch (err) {
          toast.error(err.response?.data?.message || 'Error al eliminar');
        }
      },
    });
  };

  const verDetalle = async (id) => {
    try {
      const { data } = await api.get(`/campanas/${id}`);
      setDetalle(data);
      clearInterval(pollRef.current);
      if (data.estado === 'enviando') {
        pollRef.current = setInterval(async () => {
          try {
            const { data: fresh } = await api.get(`/campanas/${id}`);
            setDetalle(fresh);
            if (fresh.estado !== 'enviando') clearInterval(pollRef.current);
          } catch { clearInterval(pollRef.current); }
        }, 4000);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error al cargar la campaña');
    }
  };

  const cerrarDetalle = () => { clearInterval(pollRef.current); setDetalle(null); setEditandoDest(null); };

  const [editandoDest, setEditandoDest] = useState(null); // { id, contacto, nombre }
  const [guardandoDest, setGuardandoDest] = useState(false);

  const empezarEdicionDest = (d) => setEditandoDest({ id: d.id, contacto: d.contacto, nombre: d.nombre || '' });

  const guardarEdicionDest = async () => {
    if (!editandoDest.contacto.trim()) { toast.error('El contacto es requerido'); return; }
    setGuardandoDest(true);
    try {
      await api.patch(`/campanas/${detalle.id}/destinatarios/${editandoDest.id}`, {
        contacto: editandoDest.contacto.trim(),
        nombre: editandoDest.nombre.trim(),
      });
      toast.success('Destinatario actualizado');
      setEditandoDest(null);
      verDetalle(detalle.id);
      fetchCampanas();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error al actualizar');
    } finally {
      setGuardandoDest(false);
    }
  };

  useEffect(() => () => clearInterval(pollRef.current), []);

  return (
    <Layout>
      <div style={{ padding: '1.5rem', maxWidth: '1100px', margin: '0 auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <div>
            <h1 style={{ fontSize: '1.5rem', fontWeight: '800', color: '#111827', marginBottom: '0.25rem' }}>📢 Difusión</h1>
            <p style={{ color: '#6b7280', fontSize: '0.875rem', margin: 0 }}>
              Envía campañas de WhatsApp personalizadas a tus prospectos y clientes.
            </p>
          </div>
          <button className="btn btn-primary" onClick={abrirNueva}>+ Nueva campaña</button>
        </div>

        <div className="card">
          {loading ? (
            <div style={{ textAlign: 'center', padding: '3rem', color: '#6b7280' }}>Cargando...</div>
          ) : campanas.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '3rem', color: '#9ca3af' }}>
              <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>📭</div>
              <p>No has creado ninguna campaña todavía.</p>
            </div>
          ) : (
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Nombre</th>
                    <th>Estado</th>
                    <th>Destinatarios</th>
                    <th>Enviados</th>
                    <th>Fallidos</th>
                    <th>Fecha</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {campanas.map(c => {
                    const est = ESTADO_BADGE[c.estado] || ESTADO_BADGE.borrador;
                    return (
                      <tr key={c.id}>
                        <td style={{ fontWeight: '600' }}>{c.nombre}</td>
                        <td>
                          <span style={{ fontSize: '0.78rem', fontWeight: '600', padding: '0.2rem 0.65rem', borderRadius: '12px', background: est.bg, color: est.color }}>
                            {est.label}
                          </span>
                        </td>
                        <td>{c.total_destinatarios}</td>
                        <td style={{ color: '#16a34a', fontWeight: '600' }}>{c.enviados}</td>
                        <td style={{ color: c.fallidos > 0 ? '#dc2626' : '#9ca3af', fontWeight: '600' }}>{c.fallidos}</td>
                        <td style={{ color: '#6b7280', fontSize: '0.85rem' }}>{new Date(c.created_at).toLocaleDateString('es-EC')}</td>
                        <td>
                          <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                            <button className="btn btn-sm btn-secondary" onClick={() => verDetalle(c.id)}>Ver</button>
                            {c.estado !== 'enviando' && (
                              <button className="btn btn-sm btn-secondary" onClick={() => abrirEditar(c.id)}>Editar</button>
                            )}
                            {c.estado === 'borrador' && (
                              <button className="btn btn-sm btn-primary" onClick={() => enviarBorrador(c.id)}>Enviar</button>
                            )}
                            {c.estado === 'pausada' && (
                              <button className="btn btn-sm btn-primary" onClick={() => enviarBorrador(c.id)}>Reanudar</button>
                            )}
                            {c.estado === 'enviando' && (
                              <button className="btn btn-sm btn-warning" onClick={() => pausarCampana(c.id)}>Pausar</button>
                            )}
                            {(c.estado === 'enviada' || c.estado === 'pausada') &&
                              (c.fallidos > 0 || (c.total_destinatarios - c.enviados - c.fallidos) > 0) && (
                              <button className="btn btn-sm btn-primary" onClick={() => reintentarCampana(c.id)}>Reintentar fallidos</button>
                            )}
                            {c.estado !== 'enviando' && (
                              <button className="btn btn-sm btn-danger" onClick={() => eliminarCampana(c.id)}>Eliminar</button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Modal nueva campaña */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '700px', width: '100%', borderRadius: '16px', display: 'flex', flexDirection: 'column', maxHeight: '90vh', overflow: 'hidden' }}>
            <div style={{ background: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)', padding: '1.5rem 1.75rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <div style={{ background: 'rgba(255,255,255,0.2)', borderRadius: '10px', width: '42px', height: '42px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.3rem' }}>📢</div>
                <div>
                  <h3 style={{ margin: 0, color: 'white', fontSize: '1.15rem', fontWeight: '700' }}>{editingId ? 'Editar campaña' : 'Nueva campaña de Difusión'}</h3>
                  <p style={{ margin: 0, color: 'rgba(255,255,255,0.75)', fontSize: '0.8rem' }}>Mensaje personalizado por WhatsApp</p>
                </div>
              </div>
              <button onClick={() => setShowModal(false)} style={{ background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: '8px', color: 'white', width: '32px', height: '32px', cursor: 'pointer', fontSize: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
            </div>

            <div className="modal-body" style={{ padding: '1.5rem 1.75rem', overflowY: 'auto', flex: 1, minHeight: 0 }}>

              {/* Mensaje */}
              <div style={{ marginBottom: '1.5rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem', paddingBottom: '0.5rem', borderBottom: '2px solid #eff6ff' }}>
                  <span style={{ background: '#eff6ff', color: '#2563eb', borderRadius: '6px', padding: '0.2rem 0.5rem', fontSize: '0.85rem', fontWeight: '700' }}>✉️ Mensaje</span>
                </div>
                <div className="form-group" style={{ marginBottom: '0.75rem' }}>
                  <label style={{ fontWeight: '600', color: '#374151', marginBottom: '0.4rem', display: 'block', fontSize: '0.875rem' }}>Nombre de la campaña</label>
                  <input type="text" style={inputStyle} value={form.nombre} onChange={e => setForm(f => ({ ...f, nombre: e.target.value }))} placeholder="Ej. Promo de fin de mes" />
                </div>
                <div className="form-group">
                  <label style={{ fontWeight: '600', color: '#374151', marginBottom: '0.4rem', display: 'block', fontSize: '0.875rem' }}>Mensaje</label>
                  <textarea rows={4} style={{ ...inputStyle, resize: 'vertical' }} value={form.mensaje} onChange={e => setForm(f => ({ ...f, mensaje: e.target.value }))} placeholder="Hola {nombre}, tenemos una promoción especial para ti..." />
                  <p style={{ fontSize: '0.75rem', color: '#9ca3af', margin: '0.35rem 0 0' }}>Usa <code>{'{nombre}'}</code> y se reemplaza por el nombre de cada contacto.</p>
                </div>
              </div>

              {/* Adjunto */}
              <div style={{ marginBottom: '1.5rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem', paddingBottom: '0.5rem', borderBottom: '2px solid #eff6ff' }}>
                  <span style={{ background: '#fdf4ff', color: '#a21caf', borderRadius: '6px', padding: '0.2rem 0.5rem', fontSize: '0.85rem', fontWeight: '700' }}>📎 Adjunto (opcional)</span>
                </div>

                <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem' }}>
                  {[
                    { id: 'ninguno', label: 'Sin adjunto' },
                    { id: 'catalogo', label: '🗂️ Desde Catálogo' },
                    { id: 'subir', label: '⬆️ Subir archivo' },
                  ].map(op => (
                    <button key={op.id} type="button"
                      className={adjuntoModo === op.id ? 'btn btn-sm btn-primary' : 'btn btn-sm btn-secondary'}
                      onClick={() => elegirModoAdjunto(op.id)}>
                      {op.label}
                    </button>
                  ))}
                </div>

                {adjuntoModo === 'catalogo' && (
                  <div>
                    {!catalogoActivo ? (
                      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                        {catalogos.length === 0 ? (
                          <p style={{ fontSize: '0.82rem', color: '#9ca3af' }}>No tienes catálogos creados todavía.</p>
                        ) : catalogos.map(cat => (
                          <button key={cat.id} type="button" className="btn btn-sm btn-secondary" onClick={() => abrirCatalogo(cat)}>
                            {cat.nombre} ({cat.total_productos})
                          </button>
                        ))}
                      </div>
                    ) : (
                      <div>
                        <button type="button" className="btn btn-sm btn-secondary" style={{ marginBottom: '0.6rem' }} onClick={() => { setCatalogoActivo(null); setProductosCatalogo([]); }}>
                          ← Volver a catálogos
                        </button>
                        {productosCatalogo.length === 0 ? (
                          <p style={{ fontSize: '0.82rem', color: '#9ca3af' }}>Este catálogo no tiene productos con imagen.</p>
                        ) : (
                          <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap', maxHeight: '220px', overflowY: 'auto' }}>
                            {productosCatalogo.map(prod => (
                              <div key={prod.id} onClick={() => elegirProductoCatalogo(prod)}
                                style={{
                                  cursor: 'pointer', border: adjunto?.url === `${origenBackend()}${prod.imagen}` ? '2px solid #2563eb' : '1.5px solid #e5e7eb',
                                  borderRadius: '8px', overflow: 'hidden', width: '90px',
                                }}>
                                <img src={prod.imagen} alt={prod.nombre} style={{ width: '90px', height: '90px', objectFit: 'cover', display: 'block' }} />
                                <p style={{ fontSize: '0.7rem', textAlign: 'center', margin: '0.2rem 0', padding: '0 0.2rem', color: '#374151', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{prod.nombre}</p>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {adjuntoModo === 'subir' && (
                  <div>
                    <input type="file" accept="image/*,video/*" onChange={subirArchivoAdjunto} disabled={subiendoArchivo} />
                    {subiendoArchivo && <p style={{ fontSize: '0.8rem', color: '#6b7280' }}>Subiendo...</p>}
                    <p style={{ fontSize: '0.75rem', color: '#9ca3af', margin: '0.35rem 0 0' }}>Imágenes hasta 8MB, videos hasta 20MB.</p>
                  </div>
                )}

                {adjunto && (
                  <div style={{ marginTop: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.75rem', background: '#f8fafc', borderRadius: '8px', padding: '0.5rem' }}>
                    {adjunto.tipo === 'video' ? (
                      <video src={adjunto.url} style={{ width: '70px', height: '70px', objectFit: 'cover', borderRadius: '6px' }} muted />
                    ) : (
                      <img src={adjunto.url} alt="Adjunto" style={{ width: '70px', height: '70px', objectFit: 'cover', borderRadius: '6px' }} />
                    )}
                    <span style={{ fontSize: '0.82rem', color: '#374151', flex: 1 }}>Adjunto listo ({adjunto.tipo})</span>
                    <button type="button" className="btn btn-sm btn-danger" onClick={() => setAdjunto(null)}>Quitar</button>
                  </div>
                )}
              </div>

              {/* Destinatarios */}
              <div style={{ marginBottom: '1.5rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem', paddingBottom: '0.5rem', borderBottom: '2px solid #eff6ff' }}>
                  <span style={{ background: '#f0fdf4', color: '#16a34a', borderRadius: '6px', padding: '0.2rem 0.5rem', fontSize: '0.85rem', fontWeight: '700' }}>👥 Destinatarios</span>
                </div>
                <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem' }}>
                  {[
                    { id: 'crm', label: '📋 Desde CRM' },
                    { id: 'excel', label: '📊 Desde Excel' },
                  ].map(op => (
                    <button key={op.id} type="button"
                      className={origenDestinatarios === op.id ? 'btn btn-sm btn-primary' : 'btn btn-sm btn-secondary'}
                      onClick={() => { setOrigenDestinatarios(op.id); setPreview([]); setSeleccionados({}); setResumenExcel(null); }}>
                      {op.label}
                    </button>
                  ))}
                </div>

                {origenDestinatarios === 'crm' && (
                  <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '0.75rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                    <div style={{ flex: '1 1 160px' }}>
                      <label style={{ fontWeight: '600', color: '#374151', marginBottom: '0.4rem', display: 'block', fontSize: '0.8rem' }}>Tipo</label>
                      <select style={inputStyle} value={filtroTipo} onChange={e => setFiltroTipo(e.target.value)}>
                        <option value="ambos">Prospectos y Clientes</option>
                        <option value="prospecto">Solo Prospectos</option>
                        <option value="cliente">Solo Clientes</option>
                      </select>
                    </div>
                    {filtroTipo !== 'cliente' && (
                      <div style={{ flex: '1 1 160px' }}>
                        <label style={{ fontWeight: '600', color: '#374151', marginBottom: '0.4rem', display: 'block', fontSize: '0.8rem' }}>Etapa (opcional)</label>
                        <select style={inputStyle} value={filtroEtapa} onChange={e => setFiltroEtapa(e.target.value)}>
                          <option value="">Todas</option>
                          {etapas.map(et => <option key={et.id} value={et.nombre}>{et.nombre}</option>)}
                        </select>
                      </div>
                    )}
                    <button type="button" className="btn btn-secondary" onClick={cargarDestinatarios} disabled={loadingPreview}>
                      {loadingPreview ? 'Cargando...' : '🔄 Cargar destinatarios'}
                    </button>
                  </div>
                )}

                {origenDestinatarios === 'excel' && (
                  <div style={{ marginBottom: '0.75rem' }}>
                    <input type="file" accept=".xlsx,.xls,.csv" onChange={cargarDestinatariosExcel} disabled={loadingPreview} />
                    <p style={{ fontSize: '0.75rem', color: '#9ca3af', margin: '0.35rem 0 0' }}>
                      El archivo debe tener columnas "nombre" y "telefono" en la primera fila.
                    </p>
                    {resumenExcel && (
                      <p style={{ fontSize: '0.78rem', color: '#374151', margin: '0.35rem 0 0' }}>
                        {resumenExcel.cargados} cargados
                        {resumenExcel.omitidosInvalidos ? ` · ${resumenExcel.omitidosInvalidos} sin nombre/teléfono` : ''}
                        {resumenExcel.omitidosDuplicados ? ` · ${resumenExcel.omitidosDuplicados} duplicados` : ''}
                      </p>
                    )}
                  </div>
                )}

                {preview.length > 0 && (
                  <>
                    <p style={{ fontSize: '0.82rem', color: '#374151', fontWeight: '600', marginBottom: '0.5rem' }}>
                      {totalSeleccionados} de {preview.length} seleccionados
                    </p>
                    <div style={{ maxHeight: '220px', overflowY: 'auto', border: '1.5px solid #e5e7eb', borderRadius: '8px' }}>
                      {preview.map(d => {
                        const key = keyDestinatario(d);
                        const checked = !!seleccionados[key];
                        const badge = d.tipo === 'cliente'
                          ? { label: 'Cliente', color: '#166534', bg: '#dcfce7' }
                          : d.tipo === 'manual'
                            ? { label: 'Excel', color: '#92400e', bg: '#fffbeb' }
                            : { label: 'Prospecto', color: '#1d4ed8', bg: '#eff6ff' };
                        return (
                          <label key={key} style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.5rem 0.75rem', borderBottom: '1px solid #f3f4f6', cursor: 'pointer', fontSize: '0.85rem' }}>
                            <input type="checkbox" checked={checked} onChange={() => toggleSeleccionado(d)} style={{ width: '15px', height: '15px', cursor: 'pointer' }} />
                            <span style={{ flex: 1 }}>{d.nombres} {d.apellidos || ''}</span>
                            <span style={{ color: '#9ca3af' }}>{d.whatsapp_number}</span>
                            <span style={{ fontSize: '0.68rem', fontWeight: '700', color: badge.color, background: badge.bg, borderRadius: '999px', padding: '0.1rem 0.5rem' }}>
                              {badge.label}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>

              {/* Velocidad */}
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem', paddingBottom: '0.5rem', borderBottom: '2px solid #eff6ff' }}>
                  <span style={{ background: '#fef9c3', color: '#b45309', borderRadius: '6px', padding: '0.2rem 0.5rem', fontSize: '0.85rem', fontWeight: '700' }}>⏱️ Velocidad de envío</span>
                </div>
                <div className="form-group" style={{ maxWidth: '220px' }}>
                  <label style={{ fontWeight: '600', color: '#374151', marginBottom: '0.4rem', display: 'block', fontSize: '0.875rem' }}>Mensajes por minuto</label>
                  <input type="number" min={1} max={10} style={inputStyle} value={form.mensajes_por_minuto}
                    onChange={e => setForm(f => ({ ...f, mensajes_por_minuto: Math.min(10, Math.max(1, parseInt(e.target.value, 10) || 1)) }))} />
                  <p style={{ fontSize: '0.75rem', color: '#9ca3af', margin: '0.35rem 0 0' }}>Máximo 10/min para reducir el riesgo de que WhatsApp bloquee el número.</p>
                </div>
              </div>
            </div>

            <div className="modal-footer" style={{ borderTop: '1px solid #e5e7eb', padding: '1rem 1.75rem', flexShrink: 0 }}>
              <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancelar</button>
              <button type="button" className="btn btn-secondary" disabled={guardando} onClick={() => crearCampana(false)}>Guardar borrador</button>
              <button type="button" className="btn btn-primary" disabled={guardando} onClick={() => crearCampana(true)}>
                {guardando ? 'Enviando...' : '📤 Enviar ahora'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal detalle / progreso */}
      {detalle && (
        <div className="modal-overlay">
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '600px', width: '100%', borderRadius: '16px', display: 'flex', flexDirection: 'column', maxHeight: '90vh', overflow: 'hidden' }}>
            <div style={{ background: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)', padding: '1.5rem 1.75rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
              <div>
                <h3 style={{ margin: 0, color: 'white', fontSize: '1.15rem', fontWeight: '700' }}>{detalle.nombre}</h3>
                <p style={{ margin: 0, color: 'rgba(255,255,255,0.75)', fontSize: '0.8rem' }}>{detalle.mensaje}</p>
              </div>
              <button onClick={cerrarDetalle} style={{ background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: '8px', color: 'white', width: '32px', height: '32px', cursor: 'pointer', fontSize: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
            </div>
            <div className="modal-body" style={{ padding: '1.5rem 1.75rem', overflowY: 'auto', flex: 1, minHeight: 0 }}>
              <div style={{ marginBottom: '1rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', color: '#374151', marginBottom: '0.4rem' }}>
                  <span>Progreso</span>
                  <span>{detalle.enviados + detalle.fallidos} / {detalle.total_destinatarios}</span>
                </div>
                <div style={{ background: '#f1f5f9', borderRadius: '999px', height: '10px', overflow: 'hidden' }}>
                  <div style={{
                    width: `${detalle.total_destinatarios ? ((detalle.enviados + detalle.fallidos) / detalle.total_destinatarios) * 100 : 0}%`,
                    background: '#2563eb', height: '100%', transition: 'width 0.3s',
                  }} />
                </div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                {(detalle.destinatarios || []).map(d => (
                  <div key={d.id} style={{ padding: '0.4rem 0.6rem', background: '#f8fafc', borderRadius: '6px', fontSize: '0.82rem' }}>
                    {editandoDest?.id === d.id ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                        <input type="text" style={{ ...inputStyle, padding: '0.4rem 0.6rem', fontSize: '0.82rem' }}
                          value={editandoDest.nombre} placeholder="Nombre"
                          onChange={e => setEditandoDest(s => ({ ...s, nombre: e.target.value }))} />
                        <input type="text" style={{ ...inputStyle, padding: '0.4rem 0.6rem', fontSize: '0.82rem' }}
                          value={editandoDest.contacto} placeholder="Número de WhatsApp"
                          onChange={e => setEditandoDest(s => ({ ...s, contacto: e.target.value }))} />
                        <div style={{ display: 'flex', gap: '0.4rem' }}>
                          <button className="btn btn-sm btn-primary" disabled={guardandoDest} onClick={guardarEdicionDest}>Guardar</button>
                          <button className="btn btn-sm btn-secondary" disabled={guardandoDest} onClick={() => setEditandoDest(null)}>Cancelar</button>
                        </div>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                          <div>{d.nombre || d.contacto}</div>
                          {d.estado === 'fallido' && d.error && (
                            <div style={{ color: '#dc2626', fontSize: '0.72rem', marginTop: '0.15rem' }}>{d.error}</div>
                          )}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <span style={{
                            fontWeight: '700', fontSize: '0.72rem',
                            color: d.estado === 'enviado' ? '#166534' : d.estado === 'fallido' ? '#dc2626' : '#6b7280',
                          }}>
                            {d.estado === 'enviado' ? '✓ Enviado' : d.estado === 'fallido' ? '✕ Falló' : '⏳ Pendiente'}
                          </span>
                          {d.estado === 'fallido' && (
                            <button className="btn btn-sm btn-secondary" onClick={() => empezarEdicionDest(d)}>Editar</button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {confirmAction && (
        <div className="modal-overlay">
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '420px', borderRadius: '16px', overflow: 'hidden' }}>
            <div style={{ background: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)', padding: '1.25rem 1.5rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <h3 style={{ margin: 0, color: 'white', fontSize: '1rem', fontWeight: '700' }}>Confirmar</h3>
              <button onClick={() => setConfirmAction(null)} style={{ background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: '8px', color: 'white', width: '28px', height: '28px', cursor: 'pointer' }}>✕</button>
            </div>
            <div className="modal-body"><p style={{ color: '#374151' }}>{confirmAction.mensaje}</p></div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setConfirmAction(null)}>No</button>
              <button className="btn btn-primary" onClick={() => { confirmAction.onConfirm(); setConfirmAction(null); }}>Sí</button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default Difusion;
